import React, { useState, useEffect, useCallback } from "react";
import { Input, Button, Form } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { API } from "aws-amplify";
import AuthRightContent from "Components/AuthRightContent";
import PublicFooter from "Components/PublicFooter";

function Signup() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState(""); // 'checking', 'available', 'taken', 'error'
  const [isLoading, setIsLoading] = useState(false);
  const [checkingTimeout, setCheckingTimeout] = useState(null);

  // Debounced username validation
  const checkUsernameAvailability = useCallback(async (usernameToCheck) => {
    if (!usernameToCheck || usernameToCheck.length === 0) {
      setUsernameStatus("");
      return;
    }

    // Validate username format
    const validUserNameRegex = /^[a-z0-9_-]+$/;
    if (!validUserNameRegex.test(usernameToCheck)) {
      setUsernameStatus("error");
      return;
    }

    setUsernameStatus("checking");

    try {
      const data = await API.get(
        "REST",
        `/company?name=${usernameToCheck}&check=true`
      );
      if (data?.isUserNameAvailable) {
        setUsernameStatus("taken");
      } else {
        setUsernameStatus("available");
      }
    } catch (error) {
      console.error("Username check failed:", error);
      setUsernameStatus("error");
    }
  }, []);

  // Handle username input changes with debouncing
  const handleUsernameChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9-]/g, "-").toLowerCase();

    setUsername(value);

    // Clear existing timeout
    if (checkingTimeout) {
      clearTimeout(checkingTimeout);
    }

    // Set new timeout for debounced checking
    const newTimeout = setTimeout(() => {
      checkUsernameAvailability(value);
    }, 500);

    setCheckingTimeout(newTimeout);
  };

  // Handle grab profile button click
  const handleGrabProfile = () => {
    if (usernameStatus === "available") {
      // Navigate to next step with username
      navigate(`/signup/details?username=${username}`);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (checkingTimeout) {
        clearTimeout(checkingTimeout);
      }
    };
  }, [checkingTimeout]);

  const getStatusMessage = () => {
    if (usernameStatus === "checking") return "Checking availability...";
    if (usernameStatus === "taken")
      return "This business seems to be taken already...Try something similar.";
    if (usernameStatus === "error") return "Invalid username format";
    return "";
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Content */}
          <div className="space-y-6">
            <div>
              <h1
                className="font-bold text-gray-900 leading-tight"
                style={{ fontSize: "32px" }}
              >
                First, claim your business
              </h1>
              <p className="text-gray-600 mt-4" style={{ fontSize: "20px" }}>
                The good ones are still available!
              </p>
            </div>

            <div className="space-y-0">
              <div className="relative">
                <Form.Item
                  hasFeedback
                  validateStatus={
                    usernameStatus === "checking"
                      ? "validating"
                      : usernameStatus === "available"
                      ? "success"
                      : ""
                  }
                >
                  <div className="flex items-center bg-gray-200 rounded-lg overflow-hidden transition-colors">
                    <span className="ps-4 py-3 text-gray-700 bg-gray-200 border-r border-none text-base">
                      inprofile.co/
                    </span>
                    <Input
                      value={username}
                      onChange={handleUsernameChange}
                      placeholder="your-business-name"
                      className="flex border-none shadow-none focus:border-none bg-gray-200 hover:bg-gray-200 py-3 pe-4 ps-1 text-base"
                      style={{ boxShadow: "none" }}
                    />
                  </div>
                </Form.Item>

                {/* Custom status message without red styling */}
                {getStatusMessage() && (
                  <div
                    className={`text-sm ${
                      usernameStatus === "available"
                        ? "text-green-600"
                        : usernameStatus === "checking"
                        ? "text-blue-600"
                        : "text-red-600"
                    }`}
                  >
                    {getStatusMessage()}
                  </div>
                )}
              </div>

              {/* Grab Profile Button */}
              {usernameStatus === "available" && (
                <Button
                  type="primary"
                  size="large"
                  onClick={handleGrabProfile}
                  className="w-full text-base font-semibold rounded-lg bg-primary"
                  loading={isLoading}
                >
                  Grab my Business
                </Button>
              )}
            </div>

            {/* Login link */}
            <div className="text-start">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 text-sm"
              >
                or log in
              </Link>
            </div>
          </div>

          {/* Right side content */}
          <AuthRightContent />
        </div>

        {/* Footer */}
        <PublicFooter />
      </div>
    </div>
  );
}

export default Signup;
